import React, { useContext, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import Modal from '../../components/common/Modal.jsx';
import { exportTableExcel } from '../../utils/exportUtils.js';
import { enviarCorreoTexto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * Modal "Status Contenedor {No. Interno}" — espejo del `modalStatus` de
 * `ListadoContenedores.jsx` de V1 (botón `fa-calendar-check` por fila): botón Excel
 * (ficha de fechas del contenedor), select de Status + "Actualizar", y una fila por
 * hito (Concepto / Fecha / Realizado) con botón de check que alterna Si/No y exige
 * que la fecha no esté vacía ("La fecha no puede estar vacia", igual que V1).
 * "Entrega Cliente" y las opciones Entregado/Cancelado solo con permiso
 * `contenedores:entrega` (`user.contenedoresEntrega` en V1).
 *
 * Igual que V1 (`updateOnlyStatus`), al "Actualizar" el status se envía un correo de
 * texto a los emails (email/email2/email3) del cliente del contenedor: asunto
 * "Nuevo Status Contenedor", fire-and-forget (un fallo del correo no afecta la actualización).
 */
export const STATUS_OPCIONES = [
  'Pendiente', 'Ficha Tecnica', 'Toque Piso', 'Telex', 'Naviera Freno Demoras', 'Cambio Patente',
  'Revalidacion', 'Pago Pedimento', 'Por Modular', 'Por Documentar Tren', 'Por subir Tren',
  'Maniobra Transportista', 'Patio Transportista', 'A1', 'PAMA', 'ROJO', 'OVT',
  'RETENIDO DESDE LLEGADA', 'Bodega Tortons', 'En Tren', 'En Programación',
];
const STATUS_ENTREGA = ['Entregado', 'Cancelado'];

// Orden y etiquetas EXACTAS del modal de V1 (Documentos está comentado en V1).
const HITOS_STATUS = [
  { key: 'bl', fecha: 'fechaBl', label: 'BL' },
  { key: 'pl', fecha: 'fechaPl', label: 'Packing List' },
  { key: 'factura', fecha: 'fechaFactura', label: 'Factura' },
  { key: 'fichaTecnica', fecha: 'fechaFichaTecnica', label: 'Ficha Tecnica' },
  { key: 'telex', fecha: 'fechaTelex', label: 'Telex' },
  { key: 'toquePiso', fecha: 'fechaToquePiso', label: 'Toque de Piso' },
  { key: 'revalidacion', fecha: 'fechaRevalidacion', label: 'Revalidacion' },
  { key: 'pagoPedimento', fecha: 'fechaPagoPedimento', label: 'Pago Pedimento' },
  { key: 'modulacion', fecha: 'fechaModulacion', label: 'Modulacion' },
  { key: 'salidaPuerto', fecha: 'fechaSalidaPuerto', label: 'Salida de Puerto' },
  { key: 'salidaRuta', fecha: 'fechaSalidaRuta', label: 'Salida Ruta' },
  { key: 'maniobras', fecha: 'fechaManiobras', label: 'Maniobras' },
  { key: 'entregaCliente', fecha: 'fechaEntregaCliente', label: 'Entrega Cliente', soloEntrega: true },
  { key: 'retornoVacio', fecha: 'fechaRetornoVacio', label: 'Retorno Vacio' },
];

export default function StatusContenedorModal({ contenedor, numeroInterno, onClose, onGuardado }) {
  const { hasPermiso } = useContext(AuthContext);
  const puedeEntrega = hasPermiso('contenedores:entrega');
  const [status, setStatus] = useState(contenedor.status || 'Pendiente');
  const [valores, setValores] = useState(() => Object.fromEntries(
    HITOS_STATUS.flatMap((h) => [[h.key, contenedor[h.key] || 'No'], [h.fecha, contenedor[h.fecha] || '']])
  ));

  function excelOC() {
    const campos = [
      ['NumeroInterno', numeroInterno],
      ['BL', valores.fechaBl], ['PL', valores.fechaPl], ['Factura', valores.fechaFactura],
      ['FichaTecnica', valores.fechaFichaTecnica], ['Telex', valores.fechaTelex],
      ['ToquePiso', valores.fechaToquePiso], ['Revalidacion', valores.fechaRevalidacion],
      ['PagoPedimento', valores.fechaPagoPedimento], ['Documentos', contenedor.fechaDocumentos],
      ['Modulacion', valores.fechaModulacion], ['SalidaPuerto', valores.fechaSalidaPuerto],
      ['SalidaRuta', valores.fechaSalidaRuta], ['Maniobras', valores.fechaManiobras],
      ['EntregaCliente', valores.fechaEntregaCliente], ['RetornoVacio', valores.fechaRetornoVacio],
      ['ObservacionesInternas', contenedor.observacionesInternas],
    ];
    exportTableExcel(
      numeroInterno,
      campos.map(([label]) => ({ key: label, label, render: (r) => r[label] })),
      [Object.fromEntries(campos)],
      { fileName: numeroInterno, sheetName: numeroInterno }
    );
  }

  async function actualizarSoloStatus() {
    try {
      await apiClient.patch(`/contenedores/${contenedor._id}`, { status });
      const cli = Array.isArray(contenedor.clientes) ? contenedor.clientes[0] : null;
      const emails = [cli?.email, cli?.email2, cli?.email3].filter((e) => e !== '' && e !== undefined && e !== null);
      if (emails.length) {
        enviarCorreoTexto({
          subject: 'Nuevo Status Contenedor',
          email: emails,
          textoHtml: plantillaCorreoV1(`Tu contenedor ${numeroInterno} / ${contenedor.contenedor} se encuentra ahora en ${status}.`),
        }).catch((e) => console.log(e));
      }
      Swal.fire({ position: 'center', icon: 'success', title: 'Actualizado con Exito', showConfirmButton: false, timer: 2000 });
      onGuardado?.();
      setTimeout(onClose, 1000);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: error.message });
    }
  }

  async function alternarHito(h) {
    const fecha = valores[h.fecha];
    if (!fecha) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'La fecha no puede estar vacia' });
      return;
    }
    try {
      await apiClient.patch(`/contenedores/${contenedor._id}`, {
        [h.key]: valores[h.key] === 'Si' ? 'No' : 'Si',
        [h.fecha]: fecha,
        status,
      });
      Swal.fire({ position: 'center', icon: 'success', title: 'Actualizado con Exito', showConfirmButton: false, timer: 2000 });
      onGuardado?.();
      setTimeout(onClose, 1000);
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: error.message });
    }
  }

  return (
    <Modal open onClose={onClose} width="max-w-3xl" title={`Status Contenedor ${numeroInterno}`}>
      <div className="space-y-3">
        <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={excelOC}>Excel</button>

        <div className="max-w-xs space-y-2">
          <label className="label">Status</label>
          <select className="select-base w-full" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPCIONES.map((s) => <option key={s} value={s}>{s}</option>)}
            {puedeEntrega && STATUS_ENTREGA.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={actualizarSoloStatus}>Actualizar</button>
        </div>

        <div className="grid grid-cols-12 gap-2 text-sm font-bold text-slate-700 pt-2">
          <div className="col-span-3">Concepto</div>
          <div className="col-span-4">Fecha</div>
          <div className="col-span-3">Realizado</div>
        </div>

        {HITOS_STATUS.filter((h) => !h.soloEntrega || puedeEntrega).map((h) => (
          <div key={h.key} className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-3 text-sm">{h.label}</div>
            <div className="col-span-4">
              <input
                type="date" className="input-base w-full" value={valores[h.fecha]}
                onChange={(e) => setValores((v) => ({ ...v, [h.fecha]: e.target.value }))}
              />
            </div>
            <div className="col-span-3 text-sm">{valores[h.key] === 'Si' ? 'Listo' : 'Pendiente'}</div>
            <div className="col-span-2">
              <button
                type="button" title="Cambiar" aria-label="Cambiar"
                onClick={() => alternarHito(h)}
                className={`p-1.5 rounded-md text-white ${valores[h.key] === 'Si' ? 'bg-primary-600' : 'bg-danger-500'}`}
              >
                <CheckCircle2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
